<template>
  <div>
    <div
      v-if="showNextTemplateButton || selectedInstanceIndex"
      class="d-flex justify-content-between mb-3"
    >
      <b-button-group size="sm">
        <b-button
          v-if="showNextTemplateButton"
          variant="outline-primary"
          @click="moveInstanceSelection(1)"
        >
          <b-icon icon="arrow-down" />
          Next template
        </b-button>
        <b-button
          v-if="selectedInstanceIndex"
          variant="outline-primary"
          @click="moveInstanceSelection(-1)"
        >
          Prev template
          <b-icon icon="arrow-up" />
        </b-button>
      </b-button-group>
      <span>{{ totalTemplateOrder }}/{{ totalTemplatesCount }}</span>
    </div>

    <b-form-group
      v-for="(field, index) in textFields"
      :key="`${field.element_id}_${index}`"
      :label-for="`field_${field.id}`"
    >
      <FormGroupCharLimit
        :label="field.name"
        :value="selectedInstance.data[field.element_id]"
        :max="getMaxCharsValue(field)"
      >
        <b-input
          :id="`field_${field.id}`"
          :value="selectedInstance.data[field.element_id]"
          @focus="setActiveObject(field)"
          @input="updateField(field, $event)"
          @click.stop
        />
      </FormGroupCharLimit>
    </b-form-group>
    <b-form-group
      v-for="(field, index) in multilineTextFields"
      :key="`${field.element_id}_${index}`"
      :label-for="`field_${field.id}`"
    >
      <FormGroupCharLimit
        :label="field.name"
        :value="selectedInstance.data[field.element_id]"
        :max="getMaxCharsValue(field)"
      >
        <b-textarea
          :id="`field_${field.id}`"
          :value="getValidatedValue(selectedInstance.data[field.element_id])"
          max-rows="8"
          @focus="setActiveObject(field)"
          @input="updateField(field, $event,selectedInstance)"
          @click.stop
        />
      </FormGroupCharLimit>
      <div
        v-if="shouldShowThisField(field)"
        class="d-flex align-items-baseline"
      >
        <b-button
          class="mt-1 btn-sm w-50 maintain-button-width"
          variant="primary"
          @click="redraw(field)"
        >
          Align to <strong>{{ JSON.parse(field.options).Option1 }}</strong>
        </b-button>

        <b-checkbox
          v-show="false"
          :checked="getAutoPositionValue(field)"
          class="ml-1 w-40"
          @change="onChangeAutoPositionValue(field, $event)"
        >
          Auto position
        </b-checkbox>
      </div>
    </b-form-group>
    <b-form-group
      v-for="(field) in additionalTextFields"
      :key="`${field.element_id}`"
      :label-for="`add_field_${field.element_id}`"
    >
      <FormGroupCharLimit
        :label="field.name"
        :value="field.text"
        :max="getMaxCharsValue(field)"
      >
        <b-input
          :id="`add_field_${field.element_id}`"
          :value="field.text"
          @focus="setActiveObject(field)"
          @input="updateField(field, $event)"
          @click.stop
        />
      </FormGroupCharLimit>
    </b-form-group>
    <b-form-group
      v-for="(field) in additionalMultilineTextFields"
      :key="`${field.element_id}`"
      :label-for="`add_field_${field.element_id}`"
    >
      <FormGroupCharLimit
        :label="field.name"
        :value="field.text"
        :max="getMaxCharsValue(field)"
      >
        <b-textarea
          :id="`add_field_${field.element_id}`"
          :value="field.text"
          max-rows="8"
          @focus="setActiveObject(field)"
          @input="updateField(field, $event)"
          @click.stop
        />
      </FormGroupCharLimit>
    </b-form-group>
    <b-form-group class="translation-form-group">
      <div class="d-flex flex-column">
        <b-form-select
          v-model="translateTargetLanguage"
          class="flex-grow-1 mr-2"
          :options="languages"
        />
        <div class="button-wrapper mt-3">
          <b-dropdown
            id="translate-options-dropdown"
            text="Translate"
            variant="primary"
          >
            <b-list-group flush>
              <b-list-group-item>
                <b-overlay
                  :show="loadingTranslate"
                  rounded
                  opacity="0.6"
                  spinner-small
                  spinner-variant="primary"
                  class="d-inline-block w-100"
                >
                  <b-button
                    class="flex-shrink-0 w-100"
                    @click="translate"
                  >
                    Translate
                  </b-button>
                </b-overlay>
              </b-list-group-item>
              <b-list-group-item>
                <b-overlay
                  :show="loadingCopyAndTranslate"
                  rounded
                  opacity="0.6"
                  spinner-small
                  spinner-variant="primary"
                  class="d-inline-block w-100"
                >
                  <b-button
                    class="flex-shrink-0 w-100"
                    @click="copyAndTranslate"
                  >
                    Copy Template & Translate
                  </b-button>
                </b-overlay>
              </b-list-group-item>
              <b-list-group-item>
                <b-overlay
                  :show="loadingCopyFieldsAndTranslate"
                  rounded
                  opacity="0.6"
                  spinner-small
                  spinner-variant="primary"
                  class="d-inline-block w-100"
                >
                  <b-button
                    class="flex-shrink-0 w-100"
                    @click="copyFieldsAndTranslate"
                  >
                    Copy Fields & Translate
                  </b-button>
                </b-overlay>
              </b-list-group-item>
            </b-list-group>
          </b-dropdown>
        </div>
      </div>
    </b-form-group>

    <b-form-group label="Global Update">
      <b-form-checkbox
        v-model="isApplyToAllGlobalUpdate"
        inline
        @click.native.prevent="confirmApplyToAllGlobal"
      >
        Apply to
      </b-form-checkbox>
      <SelectTemplateDropdown
        class="d-inline"
        :local-storage-key="'selected-templates-global-update'"
        :clear-local-storage="true"
      />
    </b-form-group>

    <b-form-group>
      <b-form-checkbox
        v-model="isRulerShown"
        class="mr-1"
        inline
        @change="onShowRulerChange"
      >
        Show ruler
      </b-form-checkbox>
      <b-icon
        id="rulerTooltip"
        class="cursor-pointer"
        icon="question-circle"
      />
      <b-tooltip
        target="rulerTooltip"
        triggers="hover"
      >
        <div class="ruler-tooltip">
          Hover the cursor on the ruler and press the left mouse button to create a line.
          <br> Hover the cursor on the horizontal ruler and press <strong>Shift + Left Mouse Button</strong> to remove the last added horizontal line.
          <br> Hover the cursor on the vertical ruler and press <strong>Shift + Left Mouse Button</strong> to remove the last added vertical line.
        </div>
      </b-tooltip>
      <b-button
        v-if="rulerLinesCount"
        v-b-tooltip.hover.viewport.noninteractive="`Reset lines`"
        variant="outline-primary"
        size="sm"
        class="ml-2"
        @click="removeAllLines"
      >
        <b-icon icon="arrow-clockwise" />
      </b-button>
    </b-form-group>
    <SelectAllObjectsButton />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { editorControlEventBroker, EVENTS } from '@frontend/group/modules/editor-control-popup/event-broker'
import { setSelectedInstance } from '@frontend/group/modules/copy-paste-object'
import { HANDLER_TYPES } from "@frontend/group/modules/translate-template";
import SelectTemplateDropdown
  from "@frontend/components/modules/layouts-page/EditorControl/components/SelectTemplateDropdown.vue";
import {GLOBAL_UPDATE_LOCAL_STORAGE_KEY, isGlobalUpdateOn} from "@frontend/constants/global-update";
import FormGroupCharLimit from "@frontend/components/common/atoms/FormGroupCharLimit.vue";
import {rulerEventsBroker, EVENTS as RULER_EVENTS} from "@frontend/group/modules/ruler/event-broker";
import SelectAllObjectsButton
  from '@frontend/components/modules/layouts-page/EditorControl/components/SelectAllObjectsButton.vue'

export default {
  name: 'FieldsModule',
  components: {
    SelectAllObjectsButton,
    FormGroupCharLimit,
    SelectTemplateDropdown,
  },

  data: () => ({
    translateTargetLanguage: 'Choose a language...',
    languages: [
      'Choose a language...',
      'Arabic',
      'Bengali',
      'Cantonese',
      'Dutch',
      'English',
      'French-CA',
      'French-FR',
      'German',
      'Hindi',
      'Italian',
      'Japanese',
      'Korean',
      'Polish',
      'Portuguese',
      'Russian',
      'Simplified Chinese',
      'Spanish',
      'Swedish',
      'Thai',
      'Traditional Chinese',
      'Turkish',
      'Vietnamese'
    ],
    loadingCopyFieldsAndTranslate: false,
    loadingCopyAndTranslate: false,
    loadingTranslate: false,
    isApplyToAllGlobalUpdate: false,
    isRulerShown: false
  }),

  computed: {
    ...mapGetters('layout', [
      'selectedInstance',
      'selectedInstanceIndex',
      'selectedInstanceFieldsWithType',
      'selectedInstanceAdditionalFields',
      'currentPagePreviews',
    ]),
    textFields () {
      return this.selectedInstanceFieldsWithType('Text')
    },
    multilineTextFields () {
      return this.selectedInstanceFieldsWithType('Multi Line Text')
    },
    additionalTextFields () {
      return this.selectedInstanceAdditionalFields.filter(field => field.type === 'Text');
    },
    additionalMultilineTextFields () {
      return this.selectedInstanceAdditionalFields.filter(field => field.type === 'Multi Line Text');
    },
    showNextTemplateButton () {
      return !!this.currentPagePreviews.at(this.selectedInstanceIndex + 1)
    },
    currentPage() {
      return +$('input[name="current_page"]').val()
    },
    perPage() {
      return +$('input[name="per_page"]').val()
    },
    totalTemplateOrder () {
      return this.selectedInstanceIndex + 1 + (this.currentPage - 1) * this.perPage
    },
    totalTemplatesCount () {
      return window.preview_data.searchTemplateDropdownItems.length
    },
    rulerLinesCount () {
      return _.sumBy(_.values(this.selectedInstance.ruler.linesByType), 'length')
    },
  },

  watch: {
    'selectedInstance.ruler.isRulerShown'(val) {
      this.isRulerShown = val;
    }
  },

  mounted() {
    this.isApplyToAllGlobalUpdate = isGlobalUpdateOn();
    this.changeGlobalUpdateIndicator()
    if (localStorage.getItem(GLOBAL_UPDATE_LOCAL_STORAGE_KEY) === null) {
      localStorage.setItem(GLOBAL_UPDATE_LOCAL_STORAGE_KEY, false);
    }
    editorControlEventBroker.on(EVENTS.TOGGLE_LOADING, this.toggleLoading.bind(this));
    editorControlEventBroker.on(EVENTS.GLOBAL_UPDATE, () => {
      this.isApplyToAllGlobalUpdate = !this.isApplyToAllGlobalUpdate;
      this.onChangeApplyToAllGlobal();
    });
    rulerEventsBroker.on(RULER_EVENTS.RULER_LINES_CHANGES, this.onRulerSettingsChanged);
    rulerEventsBroker.on(RULER_EVENTS.REINITIALIZE, this.loadRulerSettings);
    this.loadRulerSettings()
  },

  methods: {
    onChangeApplyToAllGlobal() {
      localStorage.setItem(GLOBAL_UPDATE_LOCAL_STORAGE_KEY, this.isApplyToAllGlobalUpdate);
      this.changeGlobalUpdateIndicator()
      if (this.isApplyToAllGlobalUpdate && this.selectedInstance) {
        const GLOBAL_UPDATE = 'globalUpdate'
        editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, { action:GLOBAL_UPDATE, instance: this.selectedInstance})
      }
    },
    changeGlobalUpdateIndicator() {
      const indicator = $('div#indicator-global-update');
      const globalUpdateIndicator = $('div#indicator-global-update .slider');
      if (isGlobalUpdateOn()) {
        globalUpdateIndicator.addClass('active');
      } else {
        globalUpdateIndicator.removeClass('active');
      }
      indicator.removeClass('d-none');
    },
    updateField (field, value, selectedInstance) {
      editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
        value,
        action: 'inputText',
        field,
        selectedInstance
      })
    },
    setActiveObject (field) {
      const target = _.find(this.selectedInstance.canvas.getObjects(), ['element_id', field.element_id])
      editorControlEventBroker.fire(EVENTS.SET_ACTIVE_OBJECT, { target })
    },
    shouldShowThisField(field){
      const options = $.parseJSON(field.options);
      return (options.Option1 !== null && options.Option1 !== "");
    },
    redraw(field) {
      this.setActiveObject(field);
      editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
        value: '',
        action: 'redrawText',
      })
    },
    moveInstanceSelection(step) {
      const newSelectedInstance = this.currentPagePreviews[this.selectedInstanceIndex + step]
      newSelectedInstance.canvas.wrapperEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setSelectedInstance(newSelectedInstance.id)
    },
    translate() {
      this.emitTranslateEvent(HANDLER_TYPES.TRANSLATE);
    },
    copyAndTranslate() {
      this.emitTranslateEvent(HANDLER_TYPES.COPY_AND_TRANSLATE);
    },
    copyFieldsAndTranslate() {
      this.emitTranslateEvent(HANDLER_TYPES.COPY_FIELDS_AND_TRANSLATE);
    },
    getMaxCharsValue(field) {
      let maxCharsFromBaseField = 10000;
      try {
        const options = JSON.parse(field.options)
        maxCharsFromBaseField = options['Max Chars']
      } catch (e) {
        //do nothing
      }
      return Number(this.selectedInstance.data[`${field.element_id}_max_chars`] || maxCharsFromBaseField)
    },
    emitTranslateEvent(handler) {
      if (this.translateTargetLanguage === 'Choose a language...') {
        return;
      }

      editorControlEventBroker.fire(EVENTS.TRANSLATE, {
        textFields: this.textFields,
        multilineTextFields: this.multilineTextFields,
        additionalTextFields: this.additionalTextFields,
        additionalMultilineTextFields: this.additionalMultilineTextFields,
        instance: this.selectedInstance,
        lang: this.translateTargetLanguage,
        handler: handler
      });
    },
    toggleLoading({ loadingType }) {
      this[loadingType] = !this[loadingType];
    },
    getValidatedValue(value) {
      return typeof value === 'boolean' ? '' : (value || '');
    },
    async confirmApplyToAllGlobal () {
      const confirmed = this.isApplyToAllGlobalUpdate || await this.$bvModal.msgBoxConfirm('Remember that performing this action will apply the changes to all templates', {
        title: 'Are you sure you want to perform this action?',
        okTitle: 'Yes',
        cancelTitle: 'No',
        autoFocusButton: 'ok',
        centered: true,
        contentClass: 'text-center',
      })
      if (confirmed) {
        this.isApplyToAllGlobalUpdate = !this.isApplyToAllGlobalUpdate
        this.onChangeApplyToAllGlobal()
      }
    },
    getAutoPositionValue(field) {
      if (Object.hasOwn(this.selectedInstance.data, `${field.element_id}_auto_position`)) {
        const value = this.selectedInstance.data[`${field.element_id}_auto_position`];
        return value === 'true' || value === true;
      }

      return false
    },
    onChangeAutoPositionValue(field, value) {
      this.setActiveObject(field);

      editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
        value: value,
        action: 'autoPosition',
      });
    },
    onShowRulerChange(val) {
      const ruler = this.selectedInstance.ruler;
      ruler.toggle(val);
      this.onRulerSettingsChanged()

    },
    removeAllLines () {
      this.selectedInstance.ruler.removeAllLines()
    },
    loadRulerSettings() {
      const previews = this.currentPagePreviews || [];
      previews.forEach((instance) => {
        const savedRulerSettings = instance.data?.rulerSettings || {};
        // Restore all lines
        savedRulerSettings.lines?.forEach(line => {
          instance.ruler.addLine({
            x1: line.x1,
            y1: line.y1,
            x2: line.x2,
            y2: line.y2,
            stroke: line.color,
            strokeWidth: line.thickness,
            type: line.type
          });
        });
        const isRulerShown = savedRulerSettings.isRulerShown || false;
        instance.ruler.isRulerShown = isRulerShown;
        if (isRulerShown) {
          instance.ruler.toggle(isRulerShown);
        }
      });
    },
    
    onRulerSettingsChanged() {
      const ruler = this.selectedInstance.ruler;
      const allLines = _.flatMap(['horizontal', 'vertical'], type =>
        ruler.linesByType[type].map(line => {
          const coords = line.calcCoords();
          return {
            x1: coords.bl.x,
            y1: coords.tl.y,
            x2: type === 'vertical' ? coords.bl.x : coords.br.x,
            y2: type === 'vertical' ? coords.bl.y : coords.tl.y,
            color: line.stroke,
            thickness: line.strokeWidth,
            type
          };
        })
      );
      const payload = {
        instanceId: this.selectedInstance.id,
        rulerSettings: {
          isRulerShown: ruler.isRulerShown,
          lines: allLines
        },
      };
      rulerEventsBroker.fire(RULER_EVENTS.RULER_CHANGES, payload);
    }
    
  }
}
</script>

<style scoped lang="scss">
.translation-form-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
    ::v-deep {
        ul.dropdown-menu {
            width: 100%;
        }
    }
}

  .button-wrapper {
    display: flex;
    flex-direction: column;
    row-gap: 10px;
  }

  button {
    white-space: nowrap;
  }

  .ruler-tooltip {
    max-width: 200px;
    text-align: left;
  }

  .maintain-button-width {
    min-width: max-content;
  }
</style>
